import React, { useState, useEffect } from 'react';
import {
  Table, Button, Badge, Modal, Form, Row, Col, Card, Alert
} from 'react-bootstrap';
import { FaPlus, FaEdit, FaDownload, FaEye } from 'react-icons/fa';
import axios from 'axios';

const GdprDsarManagementTab = ({ userData, onScoreUpdate, showNotification, region , refreshActivities }) => {
  const [requests, setRequests] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false); // NEW: Create modal
  const [viewingRequest, setViewingRequest] = useState(null);
  const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0, overdue: 0 });
  
  // NEW: State for creating new requests
  const [newRequest, setNewRequest] = useState({
    type: 'Data Access',
    dataSubject: '',
    details: '',
    platforms: [],
    verificationMethod: 'Email Verification'
  });

  const headers = {
    Region: region,
    UserId: userData?.['cognito:username'] 
  };

  const GDPR_REQUEST_TYPES = [
    'Data Access',
    'Data Deletion', 
    'Data Portability',
    'Data Rectification',
    'Object to Processing',
    'Restrict Processing'
  ];

  const VERIFICATION_METHODS = [
    'Email Verification',
    'ID Document Upload',
    'Security Questions',
    'Phone Verification'
  ];

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await axios.get('https://p842qcume2.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/dsar/list', { headers });
      setRequests(res.data);
      
      // Calculate stats
      const total = res.data.length;
      const pending = res.data.filter(r => r.status === 'Pending').length;
      const completed = res.data.filter(r => r.status === 'Completed').length;
      const overdue = res.data.filter(r => {
        const dueDate = new Date(r.dueDate);
        const today = new Date();
        return r.status !== 'Completed' && dueDate < today;
      }).length;

      setStats({ total, pending, completed, overdue });

      // Calculate compliance score (GDPR has 30-day response requirement)
      let score = 0;
      if (total > 0) {
        const onTime = res.data.filter(r => {
          const dueDate = new Date(r.dueDate);
          const completedDate = r.completedDate ? new Date(r.completedDate) : null;
          return r.status === 'Completed' && completedDate && completedDate <= dueDate;
        }).length;
        
        const responseRate = total > 0 ? (completed / total) * 100 : 0;
        const timelinessRate = completed > 0 ? (onTime / completed) * 100 : 0;
        const overdueRate = total > 0 ? (overdue / total) * 100 : 0;
        
        score = (responseRate * 0.5) + (timelinessRate * 0.3) - (overdueRate * 0.2);
        score = Math.max(0, Math.min(100, score));
      }

      onScoreUpdate(Math.round(score));
    } catch (error) {
      console.error('Error fetching DSAR requests:', error);
    }
  };

  // NEW: Create new DSAR request
  const handleCreateRequest = async () => {
    if (!newRequest.dataSubject || !newRequest.type) {
      showNotification('Please fill in required fields', 'danger');
      return;
    }

    try {
      const requestDate = new Date();
      const dueDate = new Date();
      dueDate.setDate(requestDate.getDate() + 30); // GDPR 30-day requirement

      const payload = {
        ...newRequest,
        requestDate: requestDate.toISOString(),
        dueDate: dueDate.toISOString(),
        status: 'Pending',
        platforms: newRequest.platforms.filter(p => p.trim() !== '')
      };

      await axios.post('https://8ngzp6d5yf.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/dsar/create', payload, { headers });
      
      await logActivity(`Created new GDPR request for ${payload.dataSubject}`, "success");
      showNotification('GDPR request created successfully!');
      
      fetchRequests();
      resetCreateForm();
    } catch (error) {
      console.error('Error creating request:', error);
      showNotification('Error creating request', 'danger');
    }
  };

  // NEW: Reset create form
  const resetCreateForm = () => {
    setNewRequest({
      type: 'Data Access',
      dataSubject: '',
      details: '',
      platforms: [],
      verificationMethod: 'Email Verification'
    });
    setShowCreateModal(false);
  };

 const handleStatusUpdate = async (requestId, newStatus) => {
  try {
    // Get the current status before updating
    const currentRequest = requests.find(r => r.requestId === requestId);
    const oldStatus = currentRequest?.status || 'Unknown';
    
    await axios.put(`https://w7b9yw5cg9.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/dsar/update/${requestId}`, 
      { status: newStatus, completedDate: newStatus === 'Completed' ? new Date().toISOString() : null }, 
      { headers }
    );
    
    // Enhanced logging with more context
    if (newStatus === 'Completed') {
      await logActivity(`Completed GDPR ${currentRequest?.type || 'request'} for ${currentRequest?.dataSubject || 'unknown user'} (Request #${requestId})`, "success");
    } else if (newStatus === 'In Progress') {
      await logActivity(`Started processing GDPR request #${requestId} (${oldStatus} → ${newStatus})`, "info");
    } else if (newStatus === 'Rejected') {
      await logActivity(`Rejected GDPR request #${requestId} (${oldStatus} → ${newStatus})`, "warning");
    } else {
      await logActivity(`Updated GDPR request #${requestId} status: ${oldStatus} → ${newStatus}`, "info");
    }
    
    showNotification(`GDPR request updated to ${newStatus}!`);
    fetchRequests();
  } catch (error) {
    console.error('Error updating request:', error);
    await logActivity(`Failed to update DSAR request ${requestId}: ${error.message}`, "error");
    showNotification('Error updating request', 'danger');
  }
};


  const exportResponse = async (requestId) => {
    try {
      const res = await axios.get(`https://idlptw70mk.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/dsar/export/${requestId}`, 
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `GDPR_Response_${requestId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      await logActivity(`Exported GDPR response for request ${requestId}`, "info");
    } catch (error) {
      console.error('Error exporting response:', error);
      showNotification('Error exporting response', 'danger');
    }
  };

const logActivity = async (message, badge = "info") => {
  
  
  try {
    // ✅ Map frontend fields to backend entity fields
    const payload = {
      action: badge,                    // badge → action
      module: "DSAR Management",        // Add module field
      details: message                  // message → details
      // userId and timestamp set by backend
    };
    
    
    const response = await axios.post(
      "https://w8wvx2z3f4.execute-api.ap-southeast-2.amazonaws.com/prod/api/activity-log/save",  // ✅ Correct endpoint
      payload,     
      { headers }  // ✅ Send headers properly
    );
    
    if (refreshActivities) {
        await refreshActivities();
      }
  } catch (err) {
    console.error('❌ Error logging DSAR management activity:', err);
    
    if (err.response) {
      console.error('🔥 Server Error Details:');
      console.error('Status:', err.response.status);
      console.error('Data:', err.response.data);
    }
    
    console.warn("Failed to log activity:", err.message);
  }
};


  const getStatusVariant = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'In Progress': return 'primary';
      case 'Pending': return 'warning';
      case 'Rejected': return 'danger';
      default: return 'secondary';
    }
  };

  const getDaysRemaining = (dueDate) => {
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <>
      <Alert variant="info">
        <strong>GDPR Data Subject Rights:</strong> EU individuals have the right to access, rectify, erase, 
        restrict processing, data portability, and object to processing. Response time: 30 days.
      </Alert>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4>{stats.total}</h4>
              <small>Total Requests</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-warning">{stats.pending}</h4>
              <small>Pending</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-success">{stats.completed}</h4>
              <small>Completed</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-danger">{stats.overdue}</h4>
              <small>Overdue</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* NEW: Add Request Button */}
      <Row className="mb-3">
        <Col>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <FaPlus /> Create New GDPR Request
          </Button>
        </Col>
      </Row>

      {/* DSAR Requests Table */}
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Request ID</th>
            <th>Type</th>
            <th>Data Subject</th>
            <th>Request Date</th>
            <th>Due Date</th>
            <th>Status</th>
            <th>Days Remaining</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {requests.length === 0 ? (
            <tr><td colSpan={8} className="text-center">No GDPR requests found.</td></tr>
          ) : (
            requests.map((r) => (
              <tr key={r.requestId}>
                <td>#{r.requestId}</td>
                <td>
                  <Badge bg={
                    r.type === 'Data Access' ? 'info' :
                    r.type === 'Data Deletion' ? 'danger' :
                    r.type === 'Data Portability' ? 'primary' : 'secondary'
                  }>
                    {r.type}
                  </Badge>
                </td>
                <td>{r.dataSubject}</td>
                <td>{new Date(r.requestDate).toLocaleDateString()}</td>
                <td>{new Date(r.dueDate).toLocaleDateString()}</td>
                <td>
                  <Badge bg={getStatusVariant(r.status)}>{r.status}</Badge>
                </td>
                <td>
                  {r.status !== 'Completed' && (
                    <span className={getDaysRemaining(r.dueDate) < 0 ? 'text-danger fw-bold' : ''}>
                      {getDaysRemaining(r.dueDate)} days
                    </span>
                  )}
                </td>
                <td>
                  <Button size="sm" variant="outline-primary" className="me-1" 
                          onClick={() => { setViewingRequest(r); setShowModal(true); }}>
                    <FaEye />
                  </Button>
                  {r.status === 'Completed' && (
                    <Button size="sm" variant="outline-success" className="me-1"
                            onClick={() => exportResponse(r.requestId)}>
                      <FaDownload />
                    </Button>
                  )}
                  {r.status !== 'Completed' && (
                    <Button size="sm" variant="outline-warning"
                            onClick={() => handleStatusUpdate(r.requestId, 'In Progress')}>
                      Process
                    </Button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* NEW: Create Request Modal */}
      <Modal show={showCreateModal} onHide={resetCreateForm} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New GDPR Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Request Type *</Form.Label>
                  <Form.Select
                    value={newRequest.type}
                    onChange={(e) => setNewRequest({...newRequest, type: e.target.value})}
                  >
                    {GDPR_REQUEST_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Verification Method</Form.Label>
                  <Form.Select
                    value={newRequest.verificationMethod}
                    onChange={(e) => setNewRequest({...newRequest, verificationMethod: e.target.value})}
                  >
                    {VERIFICATION_METHODS.map(method => (
                      <option key={method} value={method}>{method}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Data Subject Email *</Form.Label>
              <Form.Control
                type="email"
                value={newRequest.dataSubject}
                onChange={(e) => setNewRequest({...newRequest, dataSubject: e.target.value})}
                placeholder="subject@example.com"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Request Details</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newRequest.details}
                onChange={(e) => setNewRequest({...newRequest, details: e.target.value})}
                placeholder="Additional details about the request..."
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Affected Platforms/Systems</Form.Label>
              <Form.Control
                type="text"
                value={newRequest.platforms.join(', ')}
                onChange={(e) => setNewRequest({
                  ...newRequest, 
                  platforms: e.target.value.split(',').map(p => p.trim())
                })}
                placeholder="e.g., Website, Mobile App, CRM System (comma-separated)"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetCreateForm}>Cancel</Button>
          <Button variant="primary" onClick={handleCreateRequest}>
            Create Request
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Request Details Modal (existing) */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>GDPR Request Details - #{viewingRequest?.requestId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewingRequest && (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Request Type</Form.Label>
                    <Form.Control type="text" value={viewingRequest.type} readOnly />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Current Status</Form.Label>
                    <Form.Select 
                      value={viewingRequest.status}
                      onChange={(e) => handleStatusUpdate(viewingRequest.requestId, e.target.value)}
                    >
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="Rejected">Rejected</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Data Subject Email</Form.Label>
                <Form.Control type="email" value={viewingRequest.dataSubject} readOnly />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Request Details</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={3} 
                  value={viewingRequest.details || 'No additional details provided.'} 
                  readOnly 
                />
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Request Date</Form.Label>
                    <Form.Control type="text" value={new Date(viewingRequest.requestDate).toLocaleDateString()} readOnly />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Due Date (30 days)</Form.Label>
                    <Form.Control type="text" value={new Date(viewingRequest.dueDate).toLocaleDateString()} readOnly />
                  </Form.Group>
                </Col>
              </Row>
              {viewingRequest.platforms && viewingRequest.platforms.length > 0 && (
                <Form.Group className="mb-3">
                  <Form.Label>Affected Platforms/Systems</Form.Label>
                  <div>
                    {viewingRequest.platforms.map((platform, idx) => (
                      <Badge key={idx} bg="secondary" className="me-2">{platform}</Badge>
                    ))}
                  </div>
                </Form.Group>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
          {viewingRequest?.status === 'Completed' && (
            <Button variant="primary" onClick={() => exportResponse(viewingRequest.requestId)}>
              <FaDownload /> Download Response
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GdprDsarManagementTab;